import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import type { StrategyAction } from '@/types/collection-strategies';

interface ActionEditorDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (action: StrategyAction) => void;
    initialData?: StrategyAction | null;
    existingActions?: StrategyAction[];
}

export function ActionEditorDialog({
    open,
    onClose,
    onSave,
    initialData,
    existingActions = [],
}: ActionEditorDialogProps) {
    const [formData, setFormData] = useState<Partial<StrategyAction>>({
        type: 'email',
        timing: {
            type: 'days_from_trigger',
            daysFromTrigger: 0,
        },
        templateName: '',
        details: '',
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // Mock templates for different action types
    const emailTemplates = [
        'Payment Reminder - Friendly',
        'Payment Reminder - Urgent',
        'Final Notice Before Collection',
        'Payment Confirmation Request',
    ];

    const smsTemplates = [
        'SMS - Payment Due',
        'SMS - Urgent Payment',
        'SMS - Payment Link',
    ];

    // Load initial data
    useEffect(() => {
        if (open) {
            if (initialData) {
                setFormData(initialData);
            } else {
                setFormData({
                    type: 'email',
                    timing: {
                        type: 'days_from_trigger',
                        daysFromTrigger: 0,
                    },
                    templateName: '',
                    details: '',
                });
            }
            setErrors({});
        }
    }, [open, initialData]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.type) {
            newErrors.type = 'Action type is required';
        }

        if (!formData.timing?.type) {
            newErrors.timingType = 'Timing type is required';
        }

        if (formData.timing?.type === 'days_from_trigger' &&
            (formData.timing.daysFromTrigger === undefined || formData.timing.daysFromTrigger < 0)) {
            newErrors.daysFromTrigger = 'Days from trigger must be 0 or greater';
        }

        if (formData.timing?.type === 'days_from_previous' &&
            (formData.timing.daysFromPrevious === undefined || formData.timing.daysFromPrevious < 1)) {
            newErrors.daysFromPrevious = 'Days from previous action must be 1 or greater';
        }

        if ((formData.type === 'email' || formData.type === 'sms') && !formData.templateName) {
            newErrors.templateName = 'Template is required for this action type';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validateForm()) {
            toast.error('Please fix the validation errors');
            return;
        }

        const action: StrategyAction = {
            id: initialData?.id || `action-${Date.now()}`,
            type: formData.type!,
            timing: formData.timing!,
            templateName: formData.templateName,
            details: formData.details,
            order: initialData?.order || existingActions.length + 1,
        };

        onSave(action);
        toast.success(initialData ? 'Action updated successfully' : 'Action added successfully');
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    const getTemplateOptions = () => {
        switch (formData.type) {
            case 'email':
                return emailTemplates;
            case 'sms':
                return smsTemplates;
            default:
                return [];
        }
    };

    const showTemplateField = formData.type === 'email' || formData.type === 'sms';

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {initialData ? 'Edit Action' : 'Add Action'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Action Type */}
                    <div>
                        <Label>Action Type *</Label>
                        <Select
                            value={formData.type}
                            onValueChange={(value: any) => {
                                setFormData({
                                    ...formData,
                                    type: value,
                                    templateName: '', // Reset template when type changes
                                });
                                setErrors({ ...errors, type: '' });
                            }}
                        >
                            <SelectTrigger className={errors.type ? 'border-destructive' : ''}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="phone_call">Phone Call</SelectItem>
                                <SelectItem value="payment_link">Payment Link</SelectItem>
                                <SelectItem value="sms">SMS</SelectItem>
                                <SelectItem value="escalation">Escalation</SelectItem>
                                <SelectItem value="account_action">Account Action</SelectItem>
                                <SelectItem value="payment_plan">Payment Plan Offer</SelectItem>
                            </SelectContent>
                        </Select>
                        {errors.type && (
                            <p className="text-sm text-destructive mt-1">{errors.type}</p>
                        )}
                    </div>

                    {/* Timing Type */}
                    <div>
                        <Label>Timing Type *</Label>
                        <Select
                            value={formData.timing?.type}
                            onValueChange={(value: any) => {
                                setFormData({
                                    ...formData,
                                    timing: {
                                        type: value,
                                        daysFromTrigger: value === 'days_from_trigger' ? 0 : undefined,
                                        daysFromPrevious: value === 'days_from_previous' ? 1 : undefined,
                                    },
                                });
                                setErrors({ ...errors, timingType: '' });
                            }}
                        >
                            <SelectTrigger className={errors.timingType ? 'border-destructive' : ''}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="days_from_trigger">Days from Trigger</SelectItem>
                                <SelectItem value="days_from_previous">Days from Previous Action</SelectItem>
                                <SelectItem value="conditional">Conditional</SelectItem>
                            </SelectContent>
                        </Select>
                        {errors.timingType && (
                            <p className="text-sm text-destructive mt-1">{errors.timingType}</p>
                        )}
                    </div>

                    {/* Days from Trigger */}
                    {formData.timing?.type === 'days_from_trigger' && (
                        <div>
                            <Label>Days from Trigger *</Label>
                            <Input
                                type="number"
                                min="0"
                                value={formData.timing.daysFromTrigger ?? 0}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    setFormData({
                                        ...formData,
                                        timing: {
                                            ...formData.timing!,
                                            daysFromTrigger: Math.max(0, value),
                                        },
                                    });
                                    setErrors({ ...errors, daysFromTrigger: '' });
                                }}
                                className={errors.daysFromTrigger ? 'border-destructive' : ''}
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                Number of days after the strategy trigger to execute this action
                            </p>
                            {errors.daysFromTrigger && (
                                <p className="text-sm text-destructive mt-1">{errors.daysFromTrigger}</p>
                            )}
                        </div>
                    )}

                    {/* Days from Previous */}
                    {formData.timing?.type === 'days_from_previous' && (
                        <div>
                            <Label>Days from Previous Action *</Label>
                            <Input
                                type="number"
                                min="1"
                                value={formData.timing.daysFromPrevious ?? 1}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value) || 1;
                                    setFormData({
                                        ...formData,
                                        timing: {
                                            ...formData.timing!,
                                            daysFromPrevious: Math.max(1, value),
                                        },
                                    });
                                    setErrors({ ...errors, daysFromPrevious: '' });
                                }}
                                className={errors.daysFromPrevious ? 'border-destructive' : ''}
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                Number of days after the previous action to execute this action
                            </p>
                            {errors.daysFromPrevious && (
                                <p className="text-sm text-destructive mt-1">{errors.daysFromPrevious}</p>
                            )}
                        </div>
                    )}

                    {/* Conditional Info */}
                    {formData.timing?.type === 'conditional' && (
                        <div className="p-4 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">
                                Conditional actions will be executed based on specific conditions or triggers
                                defined in the strategy workflow. This is an advanced feature.
                            </p>
                        </div>
                    )}

                    {/* Template Selection */}
                    {showTemplateField && (
                        <div>
                            <Label>Template *</Label>
                            <Select
                                value={formData.templateName}
                                onValueChange={(value) => {
                                    setFormData({ ...formData, templateName: value });
                                    setErrors({ ...errors, templateName: '' });
                                }}
                            >
                                <SelectTrigger className={errors.templateName ? 'border-destructive' : ''}>
                                    <SelectValue placeholder="Select a template" />
                                </SelectTrigger>
                                <SelectContent>
                                    {getTemplateOptions().map((template) => (
                                        <SelectItem key={template} value={template}>
                                            {template}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {errors.templateName && (
                                <p className="text-sm text-destructive mt-1">{errors.templateName}</p>
                            )}
                        </div>
                    )}

                    {/* Details/Notes */}
                    <div>
                        <Label>Details/Notes</Label>
                        <Textarea
                            value={formData.details}
                            onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                            placeholder="Add any additional notes or details about this action..."
                            rows={3}
                        />
                    </div>

                    {/* Action Type Specific Info */}
                    {formData.type === 'phone_call' && (
                        <div className="p-4 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">
                                Phone calls will be scheduled for the assigned collector. The system will
                                create a task reminder at the specified time.
                            </p>
                        </div>
                    )}

                    {formData.type === 'payment_link' && (
                        <div className="p-4 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">
                                A secure payment link will be automatically generated and sent to the customer
                                via email or SMS.
                            </p>
                        </div>
                    )}

                    {formData.type === 'escalation' && (
                        <div className="p-4 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">
                                This action will escalate the case to a senior collector or manager based on
                                the assignment rules configured in the strategy.
                            </p>
                        </div>
                    )}

                    {formData.type === 'account_action' && (
                        <div className="p-4 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">
                                Account actions may include credit holds, service suspensions, or other
                                account-level changes based on your business rules.
                            </p>
                        </div>
                    )}

                    {formData.type === 'payment_plan' && (
                        <div className="p-4 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">
                                A payment plan offer will be automatically generated based on the outstanding
                                balance and customer payment history.
                            </p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave}>
                        {initialData ? 'Update Action' : 'Add Action'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
